import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowRight, BookOpen, Video, Heart, Check } from "lucide-react";
import Footer from "../components/Footer";
import TestimonialsSection from "../components/TestimonialsSection";
import { loadRazorpay } from "../lib/razorpay";
import { useCountry } from "../contexts/CountryContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VARIANT_ICON = {
  video: Video,
  book: BookOpen,
  heart: Heart,
};

/**
 * Photo Books — a separate product line (no AI generation). Customer pays
 * upfront, and theSnugglePrints team designs + ships the book offline.
 */
const PhotoBooksPage = () => {
  const navigate = useNavigate();
  const {
    isInternational,
    pricing: intlPricing,
    formatPrice,
    currency,
    convertUsdToInr,
    fxRate,
    formatInr,
  } = useCountry();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  // Coupon state: appliedCoupon is what's currently shaping the variant
  // prices. couponInput is the field the customer is typing into.
  const [appliedCoupon, setAppliedCoupon] = useState(null); // {code, label}
  const [variants, setVariants] = useState([]);
  const [couponInput, setCouponInput] = useState("");
  const [couponBusy, setCouponBusy] = useState(false);
  const [form, setForm] = useState({
    customer_name: "",
    email: "",
    phone: "",
    notes: "",
  });

  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        const r = await axios.get(`${API}/photo-books/examples`, {
          signal: ctrl.signal,
        });
        setData(r.data);
        const vs = r.data?.variants || [];
        setVariants(vs);
        // Pre-select the "Most Loved" variant (bundle) so the most popular
        // option is highlighted by default — falls back to the first variant.
        const defaultV = vs.find((v) => v.badge) || vs[0] || null;
        if (defaultV) setSelectedVariantId(defaultV.id);
        if (r.data?.default_coupon_code) {
          setAppliedCoupon({
            code: r.data.default_coupon_code,
            label: r.data.default_coupon_label || r.data.default_coupon_code,
          });
        }
      } catch {
        setData({ items: [], price: 1999, headline: "", subheadline: "", variants: [] });
      } finally {
        setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, []);

  // Apply / replace the customer-typed coupon. Hitting the backend
  // gives us per-variant final prices and the human label so the UI can
  // refresh in one step. Errors fall back to whatever was active before.
  const applyCoupon = async (codeRaw) => {
    const code = (codeRaw || "").trim().toUpperCase();
    if (!code) {
      toast.error("Enter a coupon code");
      return;
    }
    if (appliedCoupon?.code === code) {
      toast("Already applied");
      return;
    }
    setCouponBusy(true);
    try {
      const r = await axios.post(`${API}/photo-books/validate-coupon`, { code });
      setVariants(r.data.variants || []);
      setAppliedCoupon({
        code: r.data.coupon.code,
        label: r.data.coupon.label,
      });
      setCouponInput("");
      toast.success(`${r.data.coupon.label} applied`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid coupon code");
    } finally {
      setCouponBusy(false);
    }
  };

  // Revert to the auto-applied default (Inaugural Offer) — used when the
  // customer wants to remove a manually-typed code.
  const resetToDefaultCoupon = async () => {
    if (!data?.default_coupon_code) return;
    setCouponBusy(true);
    try {
      const r = await axios.post(`${API}/photo-books/validate-coupon`, {
        code: data.default_coupon_code,
      });
      setVariants(r.data.variants || []);
      setAppliedCoupon({
        code: r.data.coupon.code,
        label: r.data.coupon.label,
      });
    } catch {
      /* ignore */
    } finally {
      setCouponBusy(false);
    }
  };

  const setField = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const placeOrder = async () => {
    if (!form.customer_name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Name, email, and mobile number are required");
      return;
    }
    setSubmitting(true);
    try {
      // Build the order payload. International visitors pay in INR via
      // Razorpay at today's FX rate — the backend converts USD->INR on its
      // own using `intl_context.usd_amount` (we never trust client INR math).
      const create = await axios.post(`${API}/photo-books/order`, {
        ...form,
        variant_id: selectedVariantId,
        coupon_code: isInternational ? null : (appliedCoupon?.code || null),
        intl_context: isInternational
          ? { currency: "USD", usd_amount: finalPrice }
          : null,
      });
      const {
        razorpay_order_id,
        razorpay_key_id,
        order_id,
        amount,
        reference_number,
        variant_name,
        coupon_label,
      } = create.data;

      // Lazy-load the Razorpay SDK now that we know the customer is paying.
      // Without this, `new window.Razorpay(...)` below throws and the user
      // sees a generic "Could not start checkout" toast.
      try {
        await loadRazorpay();
      } catch {
        toast.error("Could not load payment SDK. Please retry.");
        setSubmitting(false);
        return;
      }

      // Razorpay checkout opens in a hosted popup. Once paid, we verify the
      // signature with the backend and flip the order status to "paid".
      const rzp = new window.Razorpay({
        key: razorpay_key_id,
        amount: amount * 100,
        currency: "INR",
        name: "theSnugglePrints",
        description: "Photo Book Order",
        order_id: razorpay_order_id,
        prefill: {
          name: form.customer_name,
          email: form.email,
          contact: form.phone,
        },
        theme: { color: "#C8A27A" },
        handler: async (response) => {
          try {
            await axios.post(`${API}/photo-books/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              order_id,
            });
            setFormOpen(false);
            const customerName = form.customer_name;
            const customerEmail = form.email;
            setForm({ customer_name: "", email: "", phone: "", notes: "" });
            // Send the customer to a dedicated confirmation page so they
            // have a clear receipt + next-steps view (mirrors the AI
            // portrait flow).
            navigate("/photo-books/success", {
              state: {
                order_id,
                reference_number,
                amount,
                variant_name,
                coupon_label,
                customer_name: customerName,
                email: customerEmail,
              },
              replace: true,
            });
          } catch (e) {
            toast.error("Payment verification failed. Please contact support.");
          }
        },
        modal: {
          ondismiss: () => setSubmitting(false),
        },
      });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setSubmitting(false);
    }
  };

  const items = data?.items || [];
  const selectedVariant = useMemo(
    () =>
      variants.find((v) => v.id === selectedVariantId) ||
      variants[0] ||
      null,
    [variants, selectedVariantId]
  );

  // For international visitors, swap the INR prices on each variant with
  // the tier-specific USD prices from /api/intl/pricing. The Inaugural
  // Offer is "auto-applied" by the table itself — sale_price < original_price.
  const displayedVariants = useMemo(() => {
    if (!isInternational || !intlPricing?.products) return variants;
    return variants.map((v) => {
      const intl = intlPricing.products[v.id];
      if (!intl || intl.sale_price == null) return v;
      return {
        ...v,
        mrp: intl.original_price,
        final_price: intl.sale_price,
        discount: Number((intl.original_price - intl.sale_price).toFixed(2)),
        save_label: intl.save_label || v.save_label,
        coupon_label: intlPricing.offer_name || "Inaugural Offer",
        coupon_code: "INAUGURAL_INTL",
      };
    });
  }, [variants, isInternational, intlPricing]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#C8A27A]" />
      </div>
    );
  }

  const displayedSelected =
    displayedVariants.find((v) => v.id === selectedVariantId) ||
    displayedVariants[0] || null;

  // Final price the customer will be charged. Falls back to the legacy
  // single price when variants aren't configured (older deployments).
  const finalPrice = displayedSelected?.final_price || data?.price || 1999;
  // Hero priority: admin-uploaded `hero_image_url` wins. If unset, fall back
  // to the first gallery item (rendered as `<video>` when applicable).
  const hasAdminHero = !!(data?.hero_image_url);
  const heroImageUrl = data?.hero_image_url || items[0]?.image_url || "";
  const heroIsVideo = !hasAdminHero && items[0]?.media_type === "video";
  // When the admin has uploaded a dedicated hero image, items[0] is NOT the
  // hero — so the gallery must show every item including the first one.
  // Without this guard, an uploaded video (sort_order=0) would be silently
  // skipped from both the hero and the gallery.
  const galleryItems = hasAdminHero ? items : items.slice(1);

  return (
    <div className="min-h-screen bg-background">
      <header className="glass-header py-5">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 flex items-center justify-between">
          <Link
            to="/"
            className="serif text-2xl tracking-tight text-[#1A1A1A] font-semibold"
            data-testid="photo-books-home-link"
          >
            theSnugglePrints
          </Link>
          <Link
            to="/"
            className="text-sm text-[#6B6B6B] hover:text-[#1A1A1A]"
          >
            All products →
          </Link>
        </div>
      </header>

      <main className="pt-24 pb-12">
        <section className="max-w-6xl mx-auto px-6 lg:px-10 mb-12">
          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10 items-start">
            <div>
              <p className="label-uppercase text-[#6B6B6B] mb-3">
                New product line
              </p>
              <h1
                className="serif text-4xl sm:text-6xl text-[#1A1A1A] leading-[1.05] mb-5"
                data-testid="photo-books-headline"
              >
                {data?.headline || "Custom Pet Photo Books"}
              </h1>
              <p className="text-[#3A3A3A] text-lg mb-6 max-w-xl">
                {data?.subheadline ||
                  "Premium hand-designed photo books, crafted from your memories. Our team designs every page personally."}
              </p>
              <p className="text-xs text-[#6B6B6B] mt-4 max-w-md">
                After payment we&apos;ll contact you within 12 hours to collect your
                photos and any special notes. Your book is designed by our team
                and shipped to you in 7–10 days.
              </p>
            </div>
            <div className="bg-card border border-[#EAE5DF] aspect-square overflow-hidden flex items-center justify-center">
              {heroImageUrl ? (
                heroIsVideo ? (
                  <video
                    src={heroImageUrl}
                    className="w-full h-full object-cover"
                    autoPlay
                    muted
                    loop
                    playsInline
                  />
                ) : (
                  <img
                    src={heroImageUrl}
                    alt={items[0]?.caption || "Sample photo book"}
                    className="w-full h-full object-cover"
                  />
                )
              ) : (
                <div className="text-center px-8">
                  <BookOpen className="h-16 w-16 mx-auto text-[#C8A27A] mb-3" />
                  <p className="serif text-xl text-[#6B6B6B]">
                    Sample books coming soon
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Variant picker — Storybook Video / A5 Hardcover / Bundle. The
            Father's Day discount is baked in: each card shows the strikethrough
            MRP, the discounted price, and a "Save ₹X" badge. */}
        {variants.length > 0 && (
          <section
            className="max-w-6xl mx-auto px-6 lg:px-10 mb-10"
            data-testid="photo-books-variants"
          >
            <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
              <h2 className="serif text-2xl sm:text-3xl text-[#1A1A1A]">
                Pick your format
              </h2>
              <span
                className="inline-flex items-center gap-1 bg-[#C8A27A] text-white text-xs uppercase tracking-wider px-3 py-1"
                data-testid="photo-books-coupon-badge"
              >
                <Check className="h-3 w-3" />
                {appliedCoupon?.label || "Inaugural Offer"} applied
              </span>
            </div>

            {/* Coupon input — anyone with a code (e.g. BHAIRUSFRIEND) can
                type it here and the per-variant prices below refresh. The
                default Inaugural Offer auto-applies on page load. */}
            <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="photo-books-coupon-row">
              <input
                type="text"
                value={couponInput}
                onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyCoupon(couponInput); } }}
                placeholder="Have a code? Enter it here"
                className="flex-1 min-w-0 sm:max-w-sm h-10 px-3 text-sm border border-[#EAE5DF] bg-white text-[#1A1A1A] focus:outline-none focus:border-[#C8A27A]"
                data-testid="photo-books-coupon-input"
                disabled={couponBusy}
                autoComplete="off"
              />
              <button
                type="button"
                onClick={() => applyCoupon(couponInput)}
                disabled={couponBusy || !couponInput.trim()}
                className="h-10 px-4 text-sm bg-[#1A1A1A] text-white disabled:opacity-50"
                data-testid="photo-books-coupon-apply"
              >
                {couponBusy ? "Applying…" : "Apply"}
              </button>
              {appliedCoupon &&
                data?.default_coupon_code &&
                appliedCoupon.code !== data.default_coupon_code && (
                  <button
                    type="button"
                    onClick={resetToDefaultCoupon}
                    className="text-xs text-[#9B6A2D] underline"
                    data-testid="photo-books-coupon-remove"
                  >
                    Remove {appliedCoupon.code}
                  </button>
                )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {displayedVariants.map((v) => {
                const active = v.id === selectedVariantId;
                const Icon = VARIANT_ICON[v.icon] || BookOpen;
                return (
                  <button
                    type="button"
                    key={v.id}
                    onClick={() => setSelectedVariantId(v.id)}
                    className={`relative text-left bg-card border-2 p-5 transition-all ${
                      active
                        ? "border-[#C8A27A] shadow-lg"
                        : "border-[#EAE5DF] hover:border-[#D9C7B0]"
                    }`}
                    data-testid={`photo-books-variant-${v.id}`}
                  >
                    {v.badge && (
                      <span
                        className="absolute -top-3 right-4 bg-[#D26A4E] text-white text-[10px] uppercase tracking-wider px-2 py-1"
                        data-testid={`photo-books-variant-${v.id}-badge`}
                      >
                        {v.badge}
                      </span>
                    )}
                    {active && (
                      <span className="absolute top-3 right-3 bg-[#C8A27A] text-white rounded-full p-1">
                        <Check className="h-3 w-3" />
                      </span>
                    )}
                    <div className="flex items-start gap-3 mb-3">
                      <span className="bg-[#5C6F4F] text-white rounded-full p-3 flex-shrink-0">
                        <Icon className="h-5 w-5" />
                      </span>
                      <div className="min-w-0">
                        <h3 className="serif text-lg text-[#1A1A1A] leading-tight">
                          {v.name}
                        </h3>
                      </div>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm line-through text-[#9A9A9A]">
                        {formatPrice(v.mrp)}
                      </span>
                      <span
                        className="serif text-3xl text-[#1A1A1A]"
                        data-testid={`photo-books-variant-${v.id}-price`}
                      >
                        {formatPrice(v.final_price)}
                      </span>
                    </div>
                    <span
                      className="inline-block mt-3 bg-[#D26A4E] text-white text-xs font-semibold px-3 py-1"
                      data-testid={`photo-books-variant-${v.id}-save`}
                    >
                      {v.save_label}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="mt-6 flex flex-wrap gap-3 items-center">
              <button
                type="button"
                onClick={() => setFormOpen(true)}
                className="btn-accent h-12 px-6 inline-flex items-center gap-2"
                data-testid="photo-books-order-cta"
              >
                <BookOpen className="h-4 w-4" />
                Place an order
                <ArrowRight className="h-4 w-4" />
              </button>
              {displayedSelected && (
                <p className="text-sm text-[#6B6B6B]" data-testid="photo-books-summary">
                  You&apos;ll pay{" "}
                  <span className="serif text-base text-[#1A1A1A]">
                    {formatPrice(finalPrice)}
                  </span>{" "}
                  for the {displayedSelected.name}.
                </p>
              )}
            </div>
          </section>
        )}

        {galleryItems.length > 0 && (
          <section
            className="max-w-6xl mx-auto px-6 lg:px-10 py-10"
            data-testid="photo-books-gallery"
          >
            <h2 className="serif text-2xl sm:text-3xl text-[#1A1A1A] mb-6">
              Examples we&apos;ve crafted
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {galleryItems.map((it) => (
                <div
                  key={it.id}
                  className="bg-card border border-[#EAE5DF] aspect-square overflow-hidden"
                  data-testid={`photo-book-example-${it.id}`}
                >
                  {it.media_type === "video" ? (
                    <video
                      src={it.image_url}
                      className="w-full h-full object-cover"
                      controls
                      muted
                      playsInline
                      preload="metadata"
                      data-testid={`photo-book-example-video-${it.id}`}
                    />
                  ) : (
                    <img
                      src={it.image_url}
                      alt={it.caption || "Photo book example"}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  )}
                  {it.caption && (
                    <p className="text-xs text-[#6B6B6B] p-3">
                      {it.caption}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        <TestimonialsSection className="bg-[#FAF6EF]" />
      </main>

      {/* Order intake modal */}
      {formOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => !submitting && setFormOpen(false)}
          data-testid="photo-book-form-modal"
        >
          <div
            className="bg-card max-w-md w-full p-6 sm:p-8 border border-[#EAE5DF]"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="serif text-2xl text-[#1A1A1A] mb-1">
              Order your photo book
            </h3>
            <p className="text-sm text-[#6B6B6B] mb-5">
              We&apos;ll contact you to gather photos &amp; design preferences.
            </p>

            <div className="space-y-3">
              <input
                type="text"
                placeholder="Full name"
                value={form.customer_name}
                onChange={setField("customer_name")}
                className="w-full border border-[#D9D3CA] bg-card px-4 py-3 text-sm focus:outline-none focus:border-[#C8A27A]"
                data-testid="photo-book-form-name"
              />
              <input
                type="email"
                placeholder="Email address"
                value={form.email}
                onChange={setField("email")}
                className="w-full border border-[#D9D3CA] bg-card px-4 py-3 text-sm focus:outline-none focus:border-[#C8A27A]"
                data-testid="photo-book-form-email"
              />
              <input
                type="tel"
                placeholder="Mobile number"
                value={form.phone}
                onChange={setField("phone")}
                className="w-full border border-[#D9D3CA] bg-card px-4 py-3 text-sm focus:outline-none focus:border-[#C8A27A]"
                data-testid="photo-book-form-phone"
              />
              <textarea
                rows={3}
                placeholder="Any notes?"
                value={form.notes}
                onChange={setField("notes")}
                className="w-full border border-[#D9D3CA] bg-card px-4 py-3 text-sm focus:outline-none focus:border-[#C8A27A] resize-y"
                data-testid="photo-book-form-notes"
              />
            </div>

            <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-sm text-[#6B6B6B]">
                  Total:{" "}
                  <span className="serif text-xl text-[#1A1A1A]" data-testid="photo-book-modal-total">
                    {formatPrice(finalPrice)}
                  </span>
                </p>
                {displayedSelected && displayedSelected.discount > 0 && (
                  <p className="text-xs text-[#6B6B6B]" data-testid="photo-book-modal-discount">
                    <span className="line-through">
                      {formatPrice(displayedSelected.mrp)}
                    </span>{" "}
                    · {appliedCoupon?.label || displayedSelected.coupon_label} ({displayedSelected.save_label})
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setFormOpen(false)}
                  disabled={submitting}
                  className="px-4 py-2 text-sm text-[#6B6B6B] hover:text-[#1A1A1A]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={placeOrder}
                  disabled={submitting}
                  className="btn-accent h-10 px-5 inline-flex items-center gap-2 disabled:opacity-60"
                  data-testid="photo-book-form-pay"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                    </>
                  ) : isInternational ? (
                    <>
                      Pay{" "}
                      {convertUsdToInr(finalPrice) != null
                        ? formatInr(convertUsdToInr(finalPrice))
                        : "…"}{" "}
                      ({formatPrice(finalPrice)})
                    </>
                  ) : (
                    <>Pay & Order</>
                  )}
                </button>
              </div>
            </div>

            {/* INR conversion disclosure for intl visitors. */}
            {isInternational && (
              <div
                className="mt-4 bg-[#FAF6EF] border border-[#EAE5DF] px-3 py-3 text-xs text-[#1A1A1A] leading-relaxed"
                data-testid="photo-book-intl-inr-disclosure"
              >
                Pricing shown in USD (${Number(finalPrice).toFixed(2)}).
                You&apos;ll be charged{" "}
                <strong data-testid="photo-book-intl-inr-amount">
                  {convertUsdToInr(finalPrice) != null
                    ? formatInr(convertUsdToInr(finalPrice))
                    : "…"}
                </strong>{" "}
                via Razorpay at today&apos;s conversion rate
                {fxRate ? ` (1 USD ≈ ₹${Number(fxRate).toFixed(2)})` : ""}.
                Your card may apply a small foreign-transaction fee.
              </div>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default PhotoBooksPage;
